import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RealTimeMonitor } from "@/components/RealTimeMonitor";
import { RealTimeProcessingCommunication } from '@/components/RealTimeProcessingCommunication';
import { RealTimeValidationMonitor } from '@/components/RealTimeValidationMonitor';
import { RealTimeProcessingPipeline } from '@/components/RealTimeProcessingPipeline';
import { AdvancedPerformanceAnalytics } from '@/components/AdvancedPerformanceAnalytics';
import { ProcessingAlertSystem } from '@/components/monitoring/ProcessingAlertSystem';
import { EnhancedProcessingMetrics } from '@/components/monitoring/EnhancedProcessingMetrics';
import { useRealTimeProcessing } from '@/hooks/useRealTimeProcessing';
import { Badge } from '@/components/ui/badge';
import { Activity, Zap, CheckCircle, AlertTriangle } from 'lucide-react';

export default function RealTimeMonitoring() {
  const { isConnected, metrics, activeRecoveries, events } = useRealTimeProcessing();
  const [selectedTab, setSelectedTab] = useState('overview');

  const getConnectionStatus = () => {
    return isConnected ? (
      <Badge variant="default" className="bg-green-100 text-green-800">
        <CheckCircle className="h-3 w-3 mr-1" />
        Connected
      </Badge>
    ) : (
      <Badge variant="destructive">
        <AlertTriangle className="h-3 w-3 mr-1" />
        Disconnected
      </Badge>
    );
  };

  const getActiveRecoveryCount = () => {
    return Array.from(activeRecoveries.values()).length;
  };

  const getRecentErrorCount = () => {
    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
    return events.filter(event => 
      event.severity === 'error' && 
      event.timestamp > oneHourAgo
    ).length;
  };

  return (
    <div className="space-y-6">
      <ProcessingAlertSystem />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Real-time Monitoring Dashboard</h1>
          <p className="text-muted-foreground mt-2">
            Monitor processing, validation, performance, and communications in real-time
          </p>
        </div>
        <div className="flex items-center gap-3">
          {getConnectionStatus()}
          <Badge variant="outline">
            {events.length} Events
          </Badge>
          {getActiveRecoveryCount() > 0 && (
            <Badge variant="secondary">
              {getActiveRecoveryCount()} Active Recoveries
            </Badge>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Processing Speed</p>
                <p className="text-2xl font-bold">{metrics.processingSpeed.toFixed(1)}%</p>
              </div>
              <Activity className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Error Rate</p>
                <p className="text-2xl font-bold">{metrics.errorRate.toFixed(1)}%</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Queue Length</p>
                <p className="text-2xl font-bold">{metrics.queueLength}</p>
              </div>
              <Zap className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Recent Errors</p>
                <p className="text-2xl font-bold">{getRecentErrorCount()}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Monitoring Interface */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="communication">Communication</TabsTrigger>
          <TabsTrigger value="validation">Validation</TabsTrigger>
          <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>System Overview</CardTitle>
              <CardDescription>
                Comprehensive real-time monitoring of all system components
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RealTimeMonitor />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="communication" className="space-y-4">
          <RealTimeProcessingCommunication />
        </TabsContent>

        <TabsContent value="validation" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Validation Monitoring</CardTitle>
              <CardDescription>
                Real-time monitoring of document validation processes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RealTimeValidationMonitor />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pipeline" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Processing Pipeline</CardTitle>
              <CardDescription>
                Live visualization of document processing pipeline stages
              </CardDescription>
            </CardHeader>
            <CardContent>
              <RealTimeProcessingPipeline />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="performance" className="space-y-4">
          <EnhancedProcessingMetrics />
          <Card>
            <CardHeader>
              <CardTitle>Performance Analytics</CardTitle>
              <CardDescription>
                Advanced performance monitoring and optimization recommendations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <AdvancedPerformanceAnalytics />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Processing Metrics</CardTitle>
                <CardDescription>
                  Key performance indicators for document processing
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Throughput</span>
                    <span className="text-sm text-muted-foreground">
                      {metrics.throughput.toFixed(1)} docs/day
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Avg Processing Time</span>
                    <span className="text-sm text-muted-foreground">
                      {metrics.averageProcessingTime.toFixed(1)}s
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm font-medium">Recovery Success Rate</span>
                    <span className="text-sm text-muted-foreground">
                      {metrics.recoverySuccessRate.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
                <CardDescription>
                  Latest processing events and status updates
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {events.slice(0, 5).map((event) => (
                    <div key={event.id} className="flex items-start gap-3 p-2 rounded-lg bg-muted/50">
                      <div className="flex-shrink-0 mt-0.5">
                        {event.severity === 'error' ? (
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                        ) : event.severity === 'success' ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <Activity className="h-4 w-4 text-blue-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{event.message}</p>
                        <p className="text-xs text-muted-foreground">
                          {event.timestamp.toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  {events.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">
                      No recent events
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}